import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOptionalAuth } from '@/context/AuthContext';

interface UsageData {
  count: number;
  todayCount?: number;
  canAsk: boolean;
  tier: string;
  limit: number | null;
  remaining: number | null;
  hoursSaved?: number;
  dollarsSaved?: number;
  resetPeriodDays?: number;
  lastUpdated?: number;
}

// Global cache and request deduplication for shared state across all consumers
const usageCache = new Map<string, { data: UsageData; timestamp: number }>();
const pendingUsageRequests = new Map<string, Promise<any>>();
const CACHE_DURATION = 15 * 1000; // 15 seconds - shorter for more real-time updates

export const useUsageTracking = () => {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const authLoading = auth?.loading ?? false;
  const [usageData, setUsageData] = useState<UsageData>({
    count: 0,
    canAsk: true,
    tier: 'loading',
    limit: null,
    remaining: null,
    lastUpdated: Date.now()
  });
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const prevAuthLoadingRef = useRef(authLoading);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Generate or get session ID for anonymous users
  const getSessionId = useCallback(() => {
    let sessionId = localStorage.getItem('anon_session_id') || localStorage.getItem('anonymous_session_id');
    if (!sessionId) {
      sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('anon_session_id', sessionId);
      localStorage.removeItem('anonymous_session_id');
    }
    return sessionId;
  }, []);

  const getCacheKey = useCallback(() => {
    return user ? `usage_${user.id}` : `usage_anon_${getSessionId()}`;
  }, [user, getSessionId]);

  // Force update mechanism - also invalidates cache
  const forceUpdate = useCallback(() => {
    const cacheKey = getCacheKey();
    usageCache.delete(cacheKey);
    setRefreshTrigger(prev => prev + 1);
  }, [getCacheKey]);

  const checkUsage = useCallback(async () => {
    if (authLoading) {
      return;
    }

    const cacheKey = getCacheKey();
    
    // Check cache first
    const cached = usageCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (mounted.current) {
        setUsageData(cached.data);
      }
      return cached.data;
    }

    // Check for pending request to deduplicate
    if (pendingUsageRequests.has(cacheKey)) {
      try {
        const result = await pendingUsageRequests.get(cacheKey);
        return result;
      } catch (error) {
        console.error('Error waiting for pending usage request:', error);
      }
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        if (mounted.current) setLoading(true);
        const sessionId = user ? null : getSessionId();
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        );
        
        const usagePromise = supabase.functions.invoke('check-usage', {
          body: {
            sessionId,
            userId: user?.id || null
          }
        });

        const { data, error } = await Promise.race([usagePromise, timeoutPromise]) as any;

        if (error) {
          console.error('Check usage error:', error);
          throw error;
        }
        
        if (!data) {
          throw new Error('No data returned from check-usage function');
        }
        
        const dataWithTimestamp = { ...data, lastUpdated: Date.now() };
        
        // Cache the result
        usageCache.set(cacheKey, {
          data: dataWithTimestamp,
          timestamp: Date.now()
        });
        
        if (mounted.current) {
          setUsageData(dataWithTimestamp);
        }
        return dataWithTimestamp;
      } catch (error) {
        console.error('Error checking usage:', error);
        
        // Improved fallback: check attorney/subscriber status before defaulting
        let fallbackTier = 'free';
        let fallbackLimit = 5;
        
        if (user) {
          try {
            // First check if user is an attorney (lawyers table)
            const { data: lawyerData } = await supabase
              .from('lawyers')
              .select('firm_id')
              .eq('user_id', user.id)
              .eq('is_active', true)
              .maybeSingle();
            
            if (lawyerData?.firm_id) {
              // User is an attorney - check firm subscription
              const { data: firmData } = await supabase
                .from('firms')
                .select('subscription_tier, subscription_status, is_trial_account')
                .eq('id', lawyerData.firm_id)
                .maybeSingle();
              
              if (firmData) {
                const hasActive = !firmData.is_trial_account || 
                  ['active', 'trial_active', 'trialing'].includes(firmData.subscription_status);
                
                if (hasActive) {
                  fallbackTier = firmData.subscription_tier || 'Standard (Trial)';
                  const tierLower = fallbackTier.toLowerCase();
                  if (tierLower.includes('ultimate') || tierLower.includes('enterprise')) {
                    fallbackLimit = 999999;
                  } else if (tierLower.includes('advanced')) {
                    fallbackLimit = 300;
                  } else {
                    fallbackLimit = 100; // Standard and Standard (Trial)
                  }
                }
              }
            } else {
              // Not an attorney - check subscriber status (clients)
              const { data: subscriber } = await supabase
                .from('subscribers')
                .select('subscription_tier, subscribed')
                .eq('user_id', user.id)
                .maybeSingle();
              
              if (subscriber?.subscribed && subscriber.subscription_tier) {
                fallbackTier = subscriber.subscription_tier;
                fallbackLimit = fallbackTier === 'Ultimate' ? 100 : 
                              fallbackTier === 'Advanced' ? 20 : 5;
              }
            }
          } catch (subError) {
            console.error('Error checking user subscription status:', subError);
          }
        } else {
          fallbackTier = 'anonymous';
          fallbackLimit = 3;
        }
        
        if (mounted.current) {
          setUsageData({
            count: 0,
            canAsk: true,
            tier: fallbackTier,
            limit: fallbackLimit,
            remaining: fallbackLimit,
            lastUpdated: Date.now()
          });
        }
        return null;
      } finally {
        if (mounted.current) setLoading(false);
        pendingUsageRequests.delete(cacheKey);
      }
    })();

    pendingUsageRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, [user, getSessionId, getCacheKey, authLoading]);

  const trackUsage = useCallback(async (complexity: 'simple' | 'complex' = 'simple') => {
    try {
      const sessionId = user ? null : getSessionId();
      
      const { data, error } = await supabase.functions.invoke('track-usage', {
        body: {
          sessionId,
          userId: user?.id || null,
          complexity
        }
      });

      if (error) throw error;
      
      forceUpdate();
      const refreshedData = await checkUsage();
      
      return data;
    } catch (error) {
      console.error('Error tracking usage:', error);
      forceUpdate();
      await checkUsage();
      return null;
    }
  }, [user, getSessionId, forceUpdate, checkUsage]);

  const resetUsage = useCallback(async () => {
    try {
      const sessionId = user ? null : getSessionId();
      
      const { data, error } = await supabase.functions.invoke('reset-usage', {
        body: {
          sessionId,
          userId: user?.id || null
        }
      });

      if (error) throw error;
      
      forceUpdate();
      await checkUsage();
      
      return data;
    } catch (error) {
      console.error('Error resetting usage:', error);
      return null;
    }
  }, [user, getSessionId, forceUpdate, checkUsage]);

  // Check usage on mount and when user changes (but wait for auth to load)
  useEffect(() => {
    if (!authLoading) {
      checkUsage();
    }
  }, [user?.id, authLoading, checkUsage]);

  // Force re-fetch when auth finishes loading
  useEffect(() => {
    if (prevAuthLoadingRef.current && !authLoading) {
      checkUsage();
    }
    prevAuthLoadingRef.current = authLoading;
  }, [authLoading, checkUsage]);

  return {
    usageData,
    loading,
    checkUsage,
    trackUsage,
    resetUsage
  };
};
